/**
 * OrderDetailSheet
 *
 * Right-side sheet that displays order details when an Order ID is clicked
 * in the sales orders table. Reuses the same detail cards as the order page.
 */

import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { useOrder } from '@hooks/orders';
import { useTranslation } from '@hooks/useTranslation';

import { PreviewSheet } from '../../../PreviewSheet';
import { CustomerBillToCard } from '../../../OrderDetails/CustomerBillToCard';
import { SalespersonSourceCard } from '../../../OrderDetails/SalespersonSourceCard';
import { AttioSyncStatus } from '../../../OrderDetails/AttioSyncStatus';
import { PaymentCard } from '../../../OrderDetails/PaymentCard';
import { OrderYieldCard } from '../../../OrderDetails/OrderYieldCard';
import { EventsSectionCard } from '../../../OrderDetails/EventsSectionCard';
import { CreateEventDialog } from '../../../OrderDetails/CreateEvent';
import { OrderHeaderRow } from '../../../OrderDetails/OrderHeaderRow';

import { ChevronIcon, ExternalLinkIcon } from '../../../icons';
import { MobileIconButton } from '../../../layouts/MobileButtons';

import type { CurrentUser } from '@/react/lib/capabilities';

interface OrderDetailSheetProps {
  orderId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Signed-in user, threaded to EventsSectionCard so its capability-gated
   *  actions (event 3-dot menu + Add events) render. Omitting it makes the
   *  RBAC check fail closed and hides those actions for everyone. */
  currentUser?: CurrentUser;
}

export function OrderDetailSheet({ orderId, open, onOpenChange, currentUser }: OrderDetailSheetProps) {
  // Keep the last non-null orderId so the sheet can keep rendering the order
  // during the Radix close animation (after the parent resets selectedOrderId).
  // Prefer the live orderId when present so switching orders doesn't render a
  // stale frame and status mutations target the correct order.
  const [stableOrderId, setStableOrderId] = useState<number | null>(orderId);
  useEffect(() => {
    if (orderId !== null) setStableOrderId(orderId);
  }, [orderId]);
  const activeOrderId = orderId ?? stableOrderId;
  const sheetOpen = open && orderId !== null;

  const { t } = useTranslation();
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [collapsedEventIds, setCollapsedEventIds] = useState<number[]>([]);

  const { data: order, isLoading, error, attioSyncTimedOut } = useOrder(activeOrderId ?? 0, {
    enabled: sheetOpen,
  });

  const loading = sheetOpen && isLoading && !order;
  const isCancelled = order?.order_status === 'cancelled';

  const header = order ? (
    <OrderHeaderRow
      order={order}
      headingAs="h2"
      leading={
        <>
          <MobileIconButton
            onClick={() => onOpenChange(false)}
            aria-label={t('common.close')}
            className="size-14 md:hidden"
          >
            <ChevronIcon direction="left" />
          </MobileIconButton>
          <a
            href={`/orders/${order.id}/details`}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden text-grey transition-colors hover:text-off-black md:inline-flex"
          >
            <ExternalLinkIcon />
          </a>
        </>
      }
    />
  ) : undefined;

  return (
    <>
      <PreviewSheet
        open={sheetOpen}
        onOpenChange={onOpenChange}
        loading={loading}
        header={header}
      >
        {order ? (
          <div data-testid="order-detail-sheet-body" className={cn('space-y-8', isCancelled && 'opacity-50')}>
            <div className="space-y-4">
              <h2 className="font-kapra-expanded text-2xl font-black uppercase text-off-black">
                {t('order_section_title')}
              </h2>

              <div
                data-testid="order-detail-sheet-order-section"
                className="space-y-8 rounded-input border border-dinnerplate-white bg-off-white p-6"
              >
                <CustomerBillToCard
                  orderId={order.id}
                  canGenerateDocumentsDirectly={order.documents_directly_generatable}
                  contact={order.contact}
                  organisation={order.organisation}
                  billing={order.billing}
                  draftEvents={order.draft_events}
                  hasPublishableEvents={order.has_publishable_events}
                  orderStatus={order.order_status}
                  xeroInvoiceId={order.xero_invoice_id}
                  currentUser={currentUser}
                />

                <SalespersonSourceCard
                  salesperson={order.salesperson}
                  orderSource={order.order_source}
                  paymentBadge={
                    <PaymentCard
                      paymentStatus={order.payment_status}
                      stripeChargeUrl={order.stripe_charge_url}
                    />
                  }
                  referredBy={order.referred_by}
                />

                {/* Attio takes this row's right column. Rendered whenever
                    either half has something to say. */}
                {(order.yield_data || order.attio) && (
                  <OrderYieldCard
                    attio={
                      <AttioSyncStatus
                        attio={order.attio}
                        attioSyncTimedOut={attioSyncTimedOut}
                        currentUser={currentUser}
                        orderId={order.id}
                      />
                    }
                    yieldData={order.yield_data}
                  />
                )}
              </div>
            </div>

            <EventsSectionCard
              orderId={order.id}
              currentUser={currentUser}
              onAddEvents={() => setShowCreateEvent(true)}
              actionsDisabled={isCancelled}
              collapsedEventIds={collapsedEventIds}
            />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16">
            <p className="font-inter text-sm text-chilli-red">
              {error instanceof Error ? error.message : 'Failed to load order'}
            </p>
          </div>
        ) : null}
      </PreviewSheet>

      {sheetOpen && activeOrderId !== null && (
        <CreateEventDialog
          isOpen={showCreateEvent}
          onOpenChange={setShowCreateEvent}
          orderId={activeOrderId}
          onEventsCreated={setCollapsedEventIds}
        />
      )}
    </>
  );
}
